import React from 'react';
import { Curious } from '@curi/react';
import { Helmet } from 'react-helmet';
import Slider from 'react-slick';
import LazyLoad from 'react-lazyload';
import __isEmpty from 'lodash/isEmpty';
import mixpanel from 'mixpanel-browser';

import './Home.css';
import FooCard from '../../components/elements/FooCard';
import Video from '../../components/global/Video';
import { sendAnalyticsEvent } from '../../services/analytics';
import { ExperimentContext } from '../../experiments/context';
import { getEnabledVariant } from '../../experiments/utils';
import { EXPERIMENT_PAGE_DESIGN_FLAGS } from '../../experiments/constants';
import { useFlagsHooksWrapper } from '../../featureFlags';
import isLocalhost from '../../utils/isLocalhost';
import Hero from './sections/Hero';
import WaysToCode from './sections/WaysToCode';
import Curriculum from './sections/Curriculum';
import PlanSelector from './sections/PaymentModule';
import SkillsSection from './sections/Skills';
import QuizSection from './sections/Quiz';
import KidsBecomeCreators from './sections/KidsBecomeCreators';
import HomeRedesign from './variants/Redesign';
import { usePageViewEvent } from '../../hooks/useAnalyticsHook';
import { gtmEvent, gtmPageviewEvent, gtmCtaTapEvent } from '../../services/google-tag-manager';
const imgURL = process.env.REACT_APP_CLOUDFRONT_URL;

function PageHeaders() {
  return (
    <Helmet>
      <title>Coding App for Kids | codeSpark</title>
      <meta
        name="description"
        content="codeSpark is the #1 learn-to-code app teaching kids the ABCs of coding. Designed for kids ages 3-10, codeSpark is an educational game that makes it fun to learn the basics of computer programming."
      />
      <meta
        name="keywords"
        content="Kids coding languages, Hour of code, coding for kids, kids apps, kids games, learn to code, STEM, Reading, Math, Elementary school"
      />
      <meta property="og:title" content="Coding Game for Kids - Try it Free!" />
      <meta
        property="og:description"
        content="Try the #1 learn-to-code app for kids ages 3-10. Used by over 20 million kids worldwide, kids learn coding basics with puzzles and build skills to make their own games."
      />
      <meta property="og:image" content={`${imgURL}/global/logo/codeSparkAcademy.jpg`} />
      <meta property="og:image:secure_url" content={`${imgURL}/global/logo/codeSparkAcademy.jpg`} />
      <meta name="twitter:card" content="app" />
      <meta name="twitter:site" content="@codesparkapp" />
      <meta
        name="twitter:description"
        content="Try the #1 learn-to-code app for kids 4+. Used by over 20 Million kids, codeSpark teaches coding basics through creative play and game creation. Coding improves STEM, reading, and math skills."
      />
      <meta name="twitter:app:country" content="US" />
      <meta name="twitter:app:id:iphone" content="923441570" />
      <meta name="twitter:app:id:ipad" content="923441570" />
      <meta name="twitter:app:id:googleplay" content="org.codespark.thefoos" />
    </Helmet>
  );
}

class Home extends React.Component {
  static contextType = ExperimentContext;

  constructor(props) {
    super(props);

    this.state = {
      subscriptionId: '',
      subscriptionPrice: '',
      trialDurationDays: 0,
      displayWoz: false,
      displayGlitch: false,
      displayVal: false,
      displaySarge: false,
      displayGracie: false,
    };
  }

  componentDidMount() {
    this.intervalId = setInterval(() => {
      if (window.google_optimize !== undefined) {
        const variant = window.google_optimize.get(process.env.REACT_APP_EXPERIMENT_ID);
        this.setState({ variant });

        try {
          mixpanel.track('WebPaymentsV3 Page: codeSpark Homepage', {
            'Optimize experiment variant': `${process.env.REACT_APP_EXPERIMENT_ID} - variant ${variant}`,
          });
        } catch (e) {
          console.error(e);
        }

        clearInterval(this.intervalId);
      }
    }, 100);

    // select a random foo to display whenever user refreshes the page\
    const fooArray = ['displayWoz', 'displayGlitch', 'displayVal', 'displaySarge', 'displayGracie'];
    const randomFoo = fooArray[Math.floor(Math.random() * fooArray.length)];
    this.setState({ [randomFoo]: true });

    if (isLocalhost() === false) gtmEvent({ action: 'start_pw' });
    gtmPageviewEvent({
      pageName: 'codespark.com: 7 Day Trial',
      componentProps: this.props,
    });
  }

  swapFooCard(foo) {
    switch (foo) {
      case 'woz':
        this.setState({
          displayWoz: true,
          displayGlitch: false,
          displayVal: false,
          displaySarge: false,
          displayGracie: false,
        });
        break;
      case 'gracie':
        this.setState({
          displayWoz: false,
          displayGlitch: false,
          displayVal: false,
          displaySarge: false,
          displayGracie: true,
        });
        break;
      case 'sarge':
        this.setState({
          displayWoz: false,
          displayGlitch: false,
          displayVal: false,
          displaySarge: true,
          displayGracie: false,
        });
        break;
      case 'glitch':
        this.setState({
          displayWoz: false,
          displayGlitch: true,
          displayVal: false,
          displaySarge: false,
          displayGracie: false,
        });
        break;
      case 'val':
        this.setState({
          displayWoz: false,
          displayGlitch: false,
          displayVal: true,
          displaySarge: false,
          displayGracie: false,
        });
        break;
      default:
        break;
    }
  }

  goToAccounts = (e, cta_id, extraURLParams = {}) => {
    const { passPlanTagToAccounts } = this.context.experiments;
    const { planTag, response: { location } } = this.props;
    const { query } = location;

    if (passPlanTagToAccounts) {
      extraURLParams.activePlanTag = planTag;
    }

    const extraQueryParams = new URLSearchParams(extraURLParams).toString();

    gtmCtaTapEvent({
      pageName: 'home_page',
      location_on_page: cta_id,
    });
    sendAnalyticsEvent('web_cta_tapped', {
      'app': 'web-site',
      'location_on_page': cta_id,
      'screen_name': 'home_page',
    }, true);

    const queryParams = [];

    // need to check if there are any url params and then make sure those get passed through in the links
    if (!__isEmpty(query)) {
      queryParams.push(query);
    }

    if (extraQueryParams) {
      queryParams.push(extraQueryParams)
    }

    let accountsLink = process.env.REACT_APP_SIGNUP_BASE_URL;
    if (queryParams.length) {
      accountsLink += `?${queryParams.join('&')}`
    }

    this.props.goToLink(
      e,
      accountsLink
    );
  }

  getVariantComponents = (variant) => {
    // CVR-822
    const variantComponentMapping = {
      homepageRefreshVariant1: [SkillsSection, WaysToCode, Curriculum],
      homepageRefreshVariant2: [WaysToCode, SkillsSection, Curriculum],
    };

    return variantComponentMapping[variant];
    // END CVR-822
  }

  getCtaLabel = (defaultLabel) => {
    const { updatedTrialLabels } = this.context.experiments;
    const { planLabels: { startTrialFreeLabel } } = this.props

    return updatedTrialLabels ? startTrialFreeLabel : defaultLabel;
  }

  render() {
    const { experiments } = this.context;

    // CVR-822
    const enabledHomepageRefreshVariant = getEnabledVariant(experiments, [
      'homepageRefreshVariant1',
      'homepageRefreshVariant2',
    ]);
    const variantComponents = this.getVariantComponents(enabledHomepageRefreshVariant);
    // END CVR-822

    // CVR-874
    const quizModule = experiments.quizSectionModule;
    // END CVR-874

    const {
      kidsBecomeCreatorsVariant1,
      kidsBecomeCreatorsVariant2,
    } = experiments;
    const enabledHomepageRedesignVariant = getEnabledVariant(experiments, EXPERIMENT_PAGE_DESIGN_FLAGS);

    const { planLabels } = this.props;
    const { trialTerm } = planLabels;

    // style options for slider/carousel
    const slickSettings = {
      dots: true,
      autoplay: true,
      autoplaySpeed: 7000,
      infinite: true,
      speed: 600,
      draggable: false,
      slidesToShow: 1,
      slidesToScroll: 1,
      fade: true,
      className: 'carousel-content',
    };

    if (enabledHomepageRedesignVariant) {
      return (
        <>
          <PageHeaders />
          <HomeRedesign
            onCtaClick={this.goToAccounts}
            planLabels={planLabels}
          />
        </>
      );
    }

    return (
      <div id="homepage" className="home-page-7-day-trial">
        <PageHeaders />

        <Hero
          onCtaClick={this.goToAccounts}
          deviceInfo={this.props.deviceInfo}
        />

        {/* CVR-874 */}
        {quizModule && <QuizSection onCtaClick={this.goToAccounts} />}
        {/* END CVR-874 */}

        {/* CVR-973 */}
        {kidsBecomeCreatorsVariant1 && <KidsBecomeCreators />}
        {/* END CVR-973 */}

        {/* CVR-822 */}
        {enabledHomepageRefreshVariant &&
          variantComponents.map((Component, i) => (
            <Component
              key={`variant_component_${i}`}
              onCtaClick={this.goToAccounts}
              variant={enabledHomepageRefreshVariant}
            />
          ))}
        {/* END-CVR-822 */}

        {/* CVR-822 */}
        {!enabledHomepageRefreshVariant && (
          // END CVR-822
          <div id="app-value-props" className="section app-value">
            <div className="list-with-image grid-x grid-padding-x">
              <ul className="small-order-2 medium-order-2 large-order-1">
                <li>
                  <img className="app-value-icon" src={`${imgURL}/global/icons/number-icons-1.svg`} alt="Value 1 icon" />
                  <h3 className="home-page-7-day-trial-h3">Basic problem-solving</h3>
                </li>
                <li>
                  <img className="app-value-icon" src={`${imgURL}/global/icons/number-icons-2.svg`} alt="Value 2 icon" />
                  <h3 className="home-page-7-day-trial-h3">Logical thinking skills</h3>
                </li>
                <li>
                  <img className="app-value-icon" src={`${imgURL}/global/icons/number-icons-3.svg`} alt="Value 3 icon" />
                  <h3 className="home-page-7-day-trial-h3">Profound creativity</h3>
                </li>
                <li>
                  <img className="app-value-icon" src={`${imgURL}/global/icons/number-icons-4.svg`} alt="Value 4 icon" />
                  <h3 className="home-page-7-day-trial-h3">Persistence and resilience</h3>
                </li>
                <li>
                  <img className="app-value-icon" src={`${imgURL}/global/icons/number-icons-5.svg`} alt="Value 5 icon" />
                  <h3 className="home-page-7-day-trial-h3">Big boost in confidence</h3>
                </li>
              </ul>
              <div className="video-placeholder small-order-1 medium-order-1 large-order-2">
                <Video
                  videoUrl="https://dlnipn2r79d6o.cloudfront.net/global/video/ipad_video_app_demo_blue.mp4"
                  title="Problem Solving Video"
                  autoPlay={true}
                  muted={true}
                  loop={true}
                  controls={false}
                />
              </div>
            </div>
            <h1 className="home-page-7-day-trial-h1">Five giant strides you’ll notice in a flash</h1>
            <div className="cta-button">
              <button
                className="home-button"
                data-event-category="button"
                data-event-action="homepage-app-value-props"
                data-event-label="Try codeSpark for Free"
                data-on="click"
                onClick={(e) => this.goToAccounts(e, 3)}
              >
                {this.getCtaLabel('Try codeSpark for Free!')}
                <img className="basic-arrow" src={`${imgURL}/global/icons/basic_arrow_white.svg`} alt="" />
              </button>
            </div>
          </div>
        )}

        {/* CVR-973 */}
        {kidsBecomeCreatorsVariant2 && <KidsBecomeCreators />}
        {/* END CVR-973 */}

        {/* CVR-765 */}
        <PlanSelector onCtaClick={this.goToAccounts} ctaLabel={this.getCtaLabel('Start Free Trial')} />
        {/* END CVR-765 */}

        {/* CVR-822 */}
        {!enabledHomepageRefreshVariant && !kidsBecomeCreatorsVariant2 && (
          // END CVR-822
          <div id="aspirational" className="section aspirational grid-x grid-padding-x">
            <div className="aspriational-content small-12 large-6 cell">
              <h1 className="home-page-7-day-trial-h1">One day, they’ll thank you for this head start</h1>
              <p>
                They’ll think it’s play, but you’ll know better! codeSpark’s wide-ranging games and challenges are
                designed to deliver education and coding instruction as “fun.”
              </p>
              <p>
                Your child’s early accomplishments will inspire and propel them throughout the rest of their lives. So
                prepare them today with the skills they’ll need for the world of tomorrow.
              </p>
              <button
                className='home-button'
                data-event-category="button"
                data-event-action="homepage-aspirational"
                data-event-label="Get ahead with codeSpark"
                data-on="click"
                onClick={(e) => this.goToAccounts(e, 4)}
              >
                {this.getCtaLabel('Get ahead with codeSpark')}
                <img className="basic-arrow" src={`${imgURL}/global/icons/basic_arrow_white.svg`} alt="" />
              </button>
            </div>
            <div className="aspirational-image small-12 large-6 cell">
              <img
                src={`${imgURL}/flagship/home-page/graduate-section-boy-ipad-nologo.png`}
                alt="boy wearing graduation cap"
              />
            </div>
          </div>
        )}

        {/* CVR-822 */}
        {!enabledHomepageRefreshVariant && (
          // END CVR-822
          <div id="welcome-video" className="section demo-video grid-x grid-margin-x">
            <div className="video-content small-12 medium-6 cell">
              <h1 className="home-page-7-day-trial-h1">Watch kids just like yours having a blast with codeSpark</h1>
              <div className="cta-button">
                <button
                  className='home-button'
                  data-event-category="button"
                  data-event-action="homepage-video"
                  data-event-label="Sign me up"
                  data-on="click"
                  onClick={(e) => this.goToAccounts(e, 5)}
                >
                  {this.getCtaLabel('Sign me up!')}
                  <img className="basic-arrow" src={`${imgURL}/global/icons/basic_arrow_white.svg`} alt="" />
                </button>
              </div>
            </div>
            <div className="small-12 medium-6 cell">
              <LazyLoad height={500} once offset={100}>
                <Video
                  extraClass="video-image"
                  videoUrl="https://d2bqrn11jthp5j.cloudfront.net/global/video/codeSparkOnboard720p.mp4"
                  title="Peek Inside demo"
                  poster={`${imgURL}/flagship/home-page/video-thumbnail.png`}
                  controls={true}
                >
                  Your browser does not support the video tag.
                </Video>
              </LazyLoad>
            </div>
          </div>
        )}

        <div className="section testimonial">
          <Slider {...slickSettings}>
            <div className="testimonial-block">
              <div className="comment">“My kid loves it!! Keeps him focused and entertained.”</div>
              <div className="credentials">
                <h5>Fernando J</h5>
                <div>Parent</div>
              </div>
            </div>
            <div className="testimonial-block">
              <div className="comment">“An educational game with a curriculum! A dream for any homeschooler.”</div>
              <div className="credentials">
                <h5>Jennifer R</h5>
                <div>Parent</div>
              </div>
            </div>
            <div className="testimonial-block">
              <div className="comment">
                “The design was so easy for the students that they took off on their own with minimal guidance.”
              </div>
              <div className="credentials">
                <h5>Amanda C</h5>
                <div>Educator</div>
              </div>
            </div>
            <div className="testimonial-block">
              <div className="comment">“Our top choice for starting young kids down the path of becoming coders.”</div>
              <div className="credentials">
                <div>Tech with Kids</div>
              </div>
            </div>
          </Slider>
          <div className="awards-block grid-x grid-margin-x">
            <div className="small-12 medium-6 three-awards">
              <div className="grid-x grid-margin-x align-spaced">
                <img
                  className="award-icon"
                  alt=""
                  src={`${imgURL}/flagship/teacher-landing/awards/AASL_Digital+Tools+Circle_2020.png`}
                />
                <img className="award-icon" alt="" src={`${imgURL}/flagship/teacher-landing/awards/brain-toy.png`} />
                <img
                  className="award-icon"
                  alt="KAPi Award"
                  src={`${imgURL}/flagship/how-it-works/awards/kapi-winner-transparent.png`}
                />
              </div>
            </div>
            <div className="small-12 medium-6">
              <div className="grid-x grid-margin-x align-spaced right">
                <img
                  className="award-icon no-show"
                  alt=""
                  src={`${imgURL}/flagship/how-it-works/awards/parents_choice_gold.png`}
                />
                <a
                  href="https://www.kidsafeseal.com/certifiedproducts/codespark_thefoos_app.html"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    alt="codeSpark - coding for kids is certified by the kidSAFE Seal Program."
                    src="https://www.kidsafeseal.com/sealimage/1730524102862329251/codespark_thefoos_app_large_whitetm.png"
                  />
                </a>
                <div></div>
              </div>
            </div>
          </div>
        </div>

        {/* CVR-822 */}
        {!enabledHomepageRefreshVariant && (
          // END CVR-822
          <div id="mission-value-props" className="section mission-value">
            <h2>We’re making critical skills accessible for kids everywhere</h2>
            <div className="value-prop-container grid-x grid-padding-x">
              <div className="block small-12 medium-4 cell">
                <img src={`${imgURL}/flagship/home-page/mission-interface.png`} alt="Mission Value" />
                <h3 className="home-page-7-day-trial-h3">Intuitive, word-free interface</h3>
                <p>Our patented technology lets kids learn to code before they even know how to read</p>
              </div>
              <div className="block small-12 medium-4 cell">
                <img src={`${imgURL}/flagship/home-page/mission-kids.png`} alt="Mission Value" />
                <h3 className="home-page-7-day-trial-h3">Perfect for girls and boys</h3>
                <p>Carefully designed to increase engagement in girls and close the gender gap in STEM</p>
              </div>
              <div className="block small-12 medium-4 cell">
                <img src={`${imgURL}/flagship/home-page/mission-school.png`} alt="Mission Value" />
                <h3 className="home-page-7-day-trial-h3">Subsidized for schools</h3>
                <p>Home subscriptions help us fund and integrate computer science in low-income schools</p>
              </div>
            </div>
            <div className="cta-button">
              <button
                className='home-button'
                data-event-category="button"
                data-event-action="homepage-mission-value-props"
                data-event-label="Help support our mission"
                data-on="click"
                onClick={(e) => this.goToAccounts(e, 6)}
              >
                Help support our mission
                <img className="basic-arrow" src={`${imgURL}/global/icons/basic_arrow_white.svg`} alt="" />
              </button>
            </div>
          </div>
        )}

        <div id="meet-the-foos" className="section foos-block">
          <div className="meet-the-foos grid-x grid-padding-x">
            <div className="large-1 cell"></div>
            <div className="intro small-12 medium-12 large-4 cell">
              <img className="meet-foos-image" alt="Meet The Foos" src={`${imgURL}/flagship/meet_the_foos2.png`} />
              <p>
                Don’t let their cuteness fool you -- The Foos are fun-loving characters that help kids learn complex
                concepts while feeling like they’re just playing games.
              </p>
              <p>
                Kids love The Foos and their connection with every character helps build a unique relationship and
                stronger interest in learning.
              </p>
              <p>
                The development of codeSpark is led by co-founder Joe Shochet, a former Disney
                Imagineer and Creative Director.
              </p>
              <button
                className='home-button'
                data-event-category="button"
                data-event-action="homepage-meet-the-foos"
                data-event-label="Learn with The Foos"
                data-on="click"
                onClick={(e) => this.goToAccounts(e, 7)}
              >
                Learn with The Foos!
                <img className="basic-arrow" src={`${imgURL}/global/icons/basic_arrow_white.svg`} alt="" />
              </button>
            </div>
            <div className="large-1 cell"></div>
            <div className="foo-card-section small-12 medium-12 large-4 cell">
              {this.state.displayWoz && (
                <FooCard
                  name="Woz"
                  title="Construction Foo"
                  description="A stickler for perfection and details, he is usually found restarting projects over and over and over."
                  fooUrl={`${imgURL}/flagship/home-page/foo-card-woz.png`}
                />
              )}
              {this.state.displayGracie && (
                <FooCard
                  name="Gracie"
                  title="Police Foo"
                  description="Gracie uncovers mysterious cases around Fooville one step at a time."
                  fooUrl={`${imgURL}/flagship/home-page/foo-card-gracie.png`}
                />
              )}
              {this.state.displaySarge && (
                <FooCard
                  name="Sarge"
                  title="Chef"
                  description="If a Foo craves a special meal, then Sarge is always ready to make it."
                  fooUrl={`${imgURL}/flagship/home-page/foo-card-sarge.png`}
                />
              )}
              {this.state.displayGlitch && (
                <FooCard
                  name="The Glitch"
                  title="Unknown"
                  description="Things seem to stop working when The Glitch is around but nothing kid coders can’t help solve."
                  fooUrl={`${imgURL}/flagship/home-page/foo-card-glitch.png`}
                />
              )}
              {this.state.displayVal && (
                <FooCard
                  name="Val"
                  title="Astro Foo"
                  description="Val can handle any event thrown her way as an expert in space navigation."
                  fooUrl={`${imgURL}/flagship/home-page/foo-card-val.png`}
                />
              )}

              <div className="foo-nav">
                <button className="home-button foo-button" onClick={() => this.swapFooCard('woz')}>
                  <img
                    className={this.state.displayWoz ? 'woz' : 'is-not-active woz'}
                    src={`${imgURL}/flagship/home-page/foo-card-woz.png`}
                    alt="Foo card"
                  />
                </button>
                <button className="home-button foo-button" onClick={() => this.swapFooCard('gracie')}>
                  <img
                    className={this.state.displayGracie ? 'gracie' : 'is-not-active gracie'}
                    src={`${imgURL}/flagship/home-page/foo-card-gracie.png`}
                    alt="Foo card"
                  />
                </button>
                <button className="home-button foo-button" onClick={() => this.swapFooCard('sarge')}>
                  <img
                    className={this.state.displaySarge ? 'sarge' : 'is-not-active sarge'}
                    src={`${imgURL}/flagship/home-page/foo-card-sarge.png`}
                    alt="Foo card"
                  />
                </button>
                <button className="home-button foo-button" onClick={() => this.swapFooCard('glitch')}>
                  <img
                    className={this.state.displayGlitch ? 'glitch' : 'is-not-active glitch'}
                    src={`${imgURL}/flagship/home-page/foo-card-glitch.png`}
                    alt="Foo card"
                  />
                </button>
                <button className="home-button foo-button" onClick={() => this.swapFooCard('val')}>
                  <img
                    className={this.state.displayVal ? 'val' : 'is-not-active val'}
                    src={`${imgURL}/flagship/home-page/foo-card-val.png`}
                    alt="Foo card"
                  />
                </button>
              </div>
            </div>
            <div className="large-1 cell"></div>
          </div>
          <div className="playground"></div>
        </div>

        <div id="cta" className="section call-to-action grid-x grid-padding-x">
          <div className="action-content small-12 medium-6">
            <h2>So... ready to try codeSpark?</h2>
            <p className="larger-text">Sign up now. Get your first {trialTerm} free!</p>
            <button
              className='home-button'
              data-event-category="button"
              data-event-action="homepage-cta"
              data-event-label="Let’s get started"
              data-on="click"
              onClick={(e) => this.goToAccounts(e, 8)}
            >
              Let’s get started
              <img className="basic-arrow" src={`${imgURL}/global/icons/basic_arrow_white.svg`} alt="" />
            </button>
            <p className="regular-text">
              Educators - <a href={`${process.env.REACT_APP_DASHBOARD_BASE_URL}`}>bring codeSpark to your classroom</a>
            </p>
          </div>
          <div className="action-image small-12 medium-6">
            <img src={`${imgURL}/flagship/home-page/footer-cta-ipad.png`} alt="" />
          </div>
        </div>
      </div>
    );
  }
}

const HomeWithFlags = (props) => {
  const flagProps = useFlagsHooksWrapper();
  usePageViewEvent('web_home_page');

  return <Home {...props} {...flagProps} />
}

export default props => (
  <Curious>
    {({ router, response }) => {
      return <HomeWithFlags router={router} response={response} {...props} />;
    }}
  </Curious>
);



// WEBPACK FOOTER //
// ./src/containers/Home/index.js